import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, LogOut, Globe, KeyRound, X, Eye, EyeOff } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { translations } from '../../utils/translations';
import { authApi } from '../../api';

const PasswordInput = ({ value, onChange, placeholder }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        className="w-full px-3 py-2 pr-9 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
      >
        {show ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
      </button>
    </div>
  );
};

const Header = () => {
  const navigate = useNavigate();
  const { language, setLanguage, currentUser, setCurrentUser, storeName, logoUrl } = useApp();
  const t = translations[language];

  const languages = [
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'km', name: 'ខ្មែរ', flag: '🇰🇭' }
  ];

  const currentLanguage = languages.find(lang => lang.code === language) || languages[0];
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [pwForm, setPwForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');

  const handleLogout = () => {
    const confirmText = language === 'en' ? 'Are you sure you want to logout?' :
                       language === 'km' ? 'តើអ្នកប្រាកដថាចង់ចេញមែនទេ?' :
                       '确定要退出登录吗？';
    if (window.confirm(confirmText)) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setCurrentUser(null);
      navigate('/login');
    }
  };

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    setShowLanguageMenu(false);
  };

  const handleChangePassword = async () => {
    setPwError('');
    if (!pwForm.oldPassword || !pwForm.newPassword || !pwForm.confirmPassword) {
      setPwError('请填写所有字段');
      return;
    }
    if (pwForm.newPassword.length < 6) {
      setPwError('新密码至少6位');
      return;
    }
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setPwError('两次输入的新密码不一致');
      return;
    }
    setPwLoading(true);
    try {
      const res = await authApi.changePassword({
        oldPassword: pwForm.oldPassword,
        newPassword: pwForm.newPassword,
      });
      if (res.success) {
        alert('密码修改成功，请重新登录');
        setShowPasswordDialog(false);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setCurrentUser(null);
        navigate('/login');
      } else {
        setPwError(res.message || '修改失败');
      }
    } catch (err) {
      setPwError(err.response?.data?.message || '修改失败');
    } finally {
      setPwLoading(false);
    }
  };

  const getRoleName = (role) => {
    const map = {
      admin:   { zh: '管理员', en: 'Admin',    km: 'អ្នកគ្រប់គ្រង' },
      manager: { zh: '店长',   en: 'Manager',  km: 'អ្នកគ្រប់គ្រងហាង' },
      cashier: { zh: '收银员', en: 'Cashier',  km: 'អ្នកគិតលុយ' }
    };
    const lang = language === 'en' ? 'en' : language === 'km' ? 'km' : 'zh';
    return map[role]?.[lang] || role;
  };

  return (
    <>
      <header className="bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {logoUrl ? (
                <img src={logoUrl} alt="logo"
                  className="w-8 h-8 rounded-full object-cover border-2 border-white/50" />
              ) : (
                <ShoppingCart className="w-6 h-6" />
              )}
              <div>
                <h1 className="text-lg font-bold">{storeName}</h1>
                <p className="text-xs text-blue-100">
                  {currentUser?.store_name && <span>{currentUser.store_name} · </span>}
                  {currentUser?.name} · {getRoleName(currentUser?.role)}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* 语言切换 */}
              <div className="relative">
                <button
                  onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                  className="flex items-center space-x-1 px-2 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-sm"
                >
                  <Globe className="w-4 h-4" />
                  <span className="hidden sm:inline">{currentLanguage.flag} {currentLanguage.name}</span>
                  <span className="sm:hidden">{currentLanguage.flag}</span>
                </button>
                {showLanguageMenu && (
                  <>
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => handleLanguageChange(lang.code)}
                          className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors flex items-center space-x-2 ${
                            language === lang.code ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                          }`}
                        >
                          <span className="text-lg">{lang.flag}</span>
                          <span className="font-medium">{lang.name}</span>
                          {language === lang.code && <span className="ml-auto text-blue-600">✓</span>}
                        </button>
                      ))}
                    </div>
                    <div className="fixed inset-0 z-40" onClick={() => setShowLanguageMenu(false)} />
                  </>
                )}
              </div>

              {/* 修改密码 */}
              <button
                onClick={() => {
                  setShowPasswordDialog(true);
                  setPwForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
                  setPwError('');
                }}
                className="flex items-center space-x-1 px-2 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-sm"
              >
                <KeyRound className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {language === 'en' ? 'Password' : language === 'km' ? 'លេខសម្ងាត់' : '修改密码'}
                </span>
              </button>

              {/* 退出 */}
              <button
                onClick={handleLogout}
                className="flex items-center space-x-1 px-3 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 transition-colors text-sm"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">{t.logout}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* 修改密码弹窗 */}
      {showPasswordDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="p-5 border-b flex justify-between items-center">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-blue-600" />
                {language === 'en' ? 'Change Password' : '修改密码'}
              </h3>
              <button onClick={() => setShowPasswordDialog(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {pwError && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-3 py-2 rounded-lg">
                  {pwError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'en' ? 'Current Password' : '原密码'}
                </label>
                <PasswordInput
                  value={pwForm.oldPassword}
                  onChange={e => setPwForm({ ...pwForm, oldPassword: e.target.value })}
                  placeholder={language === 'en' ? 'Enter current password' : '请输入原密码'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'en' ? 'New Password' : '新密码'}
                </label>
                <PasswordInput
                  value={pwForm.newPassword}
                  onChange={e => setPwForm({ ...pwForm, newPassword: e.target.value })}
                  placeholder={language === 'en' ? 'At least 6 characters' : '至少6位'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'en' ? 'Confirm Password' : '确认新密码'}
                </label>
                <PasswordInput
                  value={pwForm.confirmPassword}
                  onChange={e => setPwForm({ ...pwForm, confirmPassword: e.target.value })}
                  placeholder={language === 'en' ? 'Repeat new password' : '再次输入新密码'}
                />
              </div>
            </div>
            <div className="p-5 border-t flex gap-3">
              <button
                onClick={() => setShowPasswordDialog(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
              >
                {language === 'en' ? 'Cancel' : '取消'}
              </button>
              <button
                onClick={handleChangePassword}
                disabled={pwLoading}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {pwLoading ? '处理中...' : language === 'en' ? 'Confirm' : '确认修改'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;